import React from "react";
import "./style.css";

const featureList = [
    {
        eyebrow: "Food tracking",
        title: "Log meals without the spreadsheet feeling",
        body: "Track calories, macros, and nutrition in a way that feels light enough to keep doing every day.",
    },
    {
        eyebrow: "Health sync",
        title: "See intake and activity in one timeline",
        body: "Bring together meals, workout time, calories burned, and body-weight progress with optional Apple Health and Fitbit connections.",
    },
    {
        eyebrow: "Consistency",
        title: "Built for sustainable weight management",
        body: "Calo gives users a calmer way to stay aware of habits, progress, and long-term routines without a crowded interface.",
    },
];

const workflowSteps = [
    "Scan or log what you eat",
    "Review calories, macros, and intake trends",
    "Sync workouts and health metrics when you want them",
    "Stay focused with one clean daily view",
];

const heroScreens = [
    {
        className: "home-shot-left",
        src: "/static/image/homepage/home-app-shot-scan.png",
        alt: "Calo food scan screen",
        label: "Food scan",
    },
    {
        className: "home-shot-center",
        src: "/static/image/homepage/home-app-shot-diary.png",
        alt: "Calo diary screen",
        label: "Diary",
    },
    {
        className: "home-shot-right",
        src: "/static/image/homepage/home-app-shot-progress.png",
        alt: "Calo progress screen",
        label: "Progress",
    },
];

export const HomePage = () => {
    return (
        <div className="home-page">
            <header className="home-header">
                <a className="home-brand" href="/">
                    <span className="home-brand-text">Calo</span>
                </a>
                <nav className="home-nav">
                    <a href="/privacy-policy">Privacy Policy</a>
                    <a href="/terms-of-use">Terms of Use</a>
                    <a href="mailto:website@caloapp.ai">Support</a>
                </nav>
            </header>

            <main>
                <section className="home-hero">
                    <div className="home-hero-inner">
                        <div className="home-hero-copy">
                            <p className="home-eyebrow">Nutrition and health tracking</p>
                            <h1>Calo makes food logging feel lighter and more useful.</h1>
                            <p className="home-hero-body">
                                A calorie counter and nutrition tracker for people who want a
                                cleaner way to log meals, understand macros, and stay connected
                                to their health data across iPhone and Android.
                            </p>

                            <div className="home-download-group">
                                <a
                                    className="home-store-link"
                                    href="https://apps.apple.com/us/app/calo-ai-food-calorie-counter/id6447434453"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Download Calo on the App Store"
                                >
                                    <img
                                        src="/static/image/homepage/app-store-en.png"
                                        alt="Download on the App Store"
                                    />
                                </a>
                                <a
                                    className="home-store-link"
                                    href="https://play.google.com/store/apps/details?id=calo.ai.calorie.counter.weight.loss.diet.nutrition.macro.tracker.fasting"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Get Calo on Google Play"
                                >
                                    <img
                                        src="/static/image/homepage/google-play-en.png"
                                        alt="Get it on Google Play"
                                    />
                                </a>
                            </div>

                            <div className="home-hero-links">
                                <a className="home-text-link" href="/app">App details</a>
                                <a className="home-text-link" href="mailto:website@caloapp.ai">Contact support</a>
                            </div>
                        </div>

                        <div className="home-hero-visual">
                            <div className="home-shot-stage" aria-label="Calo app screenshots">
                                {heroScreens.map(screen => (
                                    <figure
                                        className={`home-shot ${screen.className}`}
                                        key={screen.src}
                                    >
                                        <img src={screen.src} alt={screen.alt} />
                                    </figure>
                                ))}
                            </div>
                            <div className="home-shot-summary" aria-hidden="true">
                                {heroScreens.map(screen => (
                                    <span className="home-shot-pill" key={screen.label}>
                                        {screen.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="home-section">
                    <div className="home-section-head">
                        <p className="home-eyebrow">Why people use Calo</p>
                        <h2>One app for calories, macros, meals, and progress.</h2>
                    </div>
                    <div className="home-feature-grid">
                        {featureList.map(item => (
                            <article className="home-feature" key={item.title}>
                                <p className="home-feature-eyebrow">{item.eyebrow}</p>
                                <h3>{item.title}</h3>
                                <p>{item.body}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="home-section home-section-split">
                    <div className="home-split-copy">
                        <p className="home-eyebrow">How it fits into the day</p>
                        <h2>Designed around the routine people actually keep.</h2>
                        <p className="home-support-copy">
                            Calo is meant to stay useful after the first week. The experience is
                            built around daily logging, clear summaries, and optional sync from
                            health providers instead of cluttered dashboards.
                        </p>
                    </div>
                    <div className="home-workflow">
                        {workflowSteps.map(step => (
                            <div className="home-workflow-step" key={step}>
                                <span className="home-workflow-dot" />
                                <p>{step}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="home-final">
                    <div className="home-final-copy">
                        <p className="home-eyebrow">Download Calo</p>
                        <h2>Start with the store that fits your device.</h2>
                        <p>
                            Available on iPhone and Android. Privacy policy, support, and app
                            details are always available on caloapp.ai.
                        </p>
                    </div>
                    <div className="home-final-actions">
                        <a
                            className="home-store-link"
                            href="https://apps.apple.com/us/app/calo-ai-food-calorie-counter/id6447434453"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Download Calo on the App Store"
                        >
                            <img
                                src="/static/image/homepage/app-store-en.png"
                                alt="Download on the App Store"
                            />
                        </a>
                        <a
                            className="home-store-link"
                            href="https://play.google.com/store/apps/details?id=calo.ai.calorie.counter.weight.loss.diet.nutrition.macro.tracker.fasting"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Get Calo on Google Play"
                        >
                            <img
                                src="/static/image/homepage/google-play-en.png"
                                alt="Get it on Google Play"
                            />
                        </a>
                    </div>
                </section>
            </main>

            <footer className="home-footer">
                <p>Next Vision Limited operates the Calo mobile application.</p>
                <div className="home-footer-links">
                    <a href="/app">App details</a>
                    <a href="/privacy-policy">Privacy Policy</a>
                    <a href="/terms-of-use">Terms of Use</a>
                </div>
            </footer>
        </div>
    );
};
